import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    fetchJob, jobIsActive, jobFailureMessage,
    readPlannerJobWatch, clearPlannerJobWatch,
    PHASE_DONE,
} from '../../utils/plannerJobs';

/**
 * Owner, verbatim: "once analyze shipment button is pressed for ltl it should hand off to the
 * server - can we use toast to pop a notification up if the user is on a different page?"
 *
 * Mounted once at app level (App.js) so it lives on every page, including the planner itself.
 * `runAnalyzeJob` (plannerJobs.js) registers every analyze kickoff into localStorage; this
 * component is the other half -- it polls those watched jobs and pops a toast when one finishes,
 * no matter which page the operator has since navigated to.
 */
const POLL_INTERVAL_MS = 10000;
const SUCCESS_AUTO_DISMISS_MS = 30000;

let toastSeq = 0;

export default function PlannerJobToaster() {
    const [toasts, setToasts] = useState([]);
    const navigate = useNavigate();
    const dismissTimers = useRef({});

    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        if (dismissTimers.current[id]) {
            clearTimeout(dismissTimers.current[id]);
            delete dismissTimers.current[id];
        }
    }, []);

    const addToast = useCallback((toast) => {
        const id = ++toastSeq;
        setToasts((prev) => [...prev, { ...toast, id }]);
        if (toast.kind === 'success') {
            // Failures stay until the operator dismisses them -- a failed analyze must not
            // silently disappear from a page they are not looking at.
            dismissTimers.current[id] = setTimeout(() => dismiss(id), SUCCESS_AUTO_DISMISS_MS);
        }
    }, [dismiss]);

    const poll = useCallback(async () => {
        // A hidden tab must not spend the operator's battery/data polling for a toast nobody can
        // see yet -- it will poll again as soon as the tab becomes visible.
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        const watched = readPlannerJobWatch();
        if (!watched.length) return;
        for (const entry of watched) {
            let job = null;
            try {
                // eslint-disable-next-line no-await-in-loop
                job = await fetchJob(entry.jobId);
            } catch (err) {
                continue; // transient read failure -- leave it watched for the next tick
            }
            if (!job || jobIsActive(job)) continue;
            clearPlannerJobWatch(entry.jobId);
            if (job.phase === PHASE_DONE) {
                addToast({
                    kind: 'success',
                    jobId: entry.jobId,
                    message: 'Shipment analyze finished — open the planner',
                });
            } else {
                const reason = jobFailureMessage(job) || 'analyze failed';
                const firstLine = String(reason).split('\n')[0];
                addToast({
                    kind: 'error',
                    jobId: entry.jobId,
                    message: `Shipment analyze FAILED — ${firstLine}`,
                });
            }
        }
    }, [addToast]);

    useEffect(() => {
        poll();
        const interval = setInterval(poll, POLL_INTERVAL_MS);
        const timers = dismissTimers.current;
        return () => {
            clearInterval(interval);
            Object.values(timers).forEach(clearTimeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!toasts.length) return null;

    return (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-80">
            {toasts.map((t) => (
                <div
                    key={t.id}
                    role="alert"
                    className={`bg-white border rounded-lg shadow-lg p-3 flex items-start gap-2 border-l-4 ${
                        t.kind === 'success' ? 'border-l-green-500' : 'border-l-red-500'
                    }`}
                >
                    <div className="flex-1 min-w-0 text-sm text-gray-800">
                        <p className="break-words">{t.message}</p>
                        <button
                            type="button"
                            onClick={() => { dismiss(t.id); navigate('/shipment-planner'); }}
                            className={`mt-1 text-xs font-medium underline ${
                                t.kind === 'success' ? 'text-green-600' : 'text-red-600'
                            }`}
                        >
                            Open planner
                        </button>
                    </div>
                    <button
                        type="button"
                        aria-label="Dismiss"
                        onClick={() => dismiss(t.id)}
                        className="text-gray-400 hover:text-gray-600 leading-none"
                    >
                        ×
                    </button>
                </div>
            ))}
        </div>
    );
}
